import { createMutation, createQuery } from "react-query-kit";
import { api } from "../../api";
import type {
  WBEAuthLoginRequest,
  WBEAuthSessionResponse,
  WBEAuthSignupRequest,
  WBEAuthUpdateLanguageRequest,
  WBEPasswordResetCompleteRequest,
  WBEPasswordResetRequest,
  WBEPasswordResetResponse,
} from "../../contracts/wbe-auth";

const authPath = (
  siteId: string,
  action: "language" | "login" | "logout" | "session" | "signup",
) => `/api/public/wbe-auth/${siteId}/${action}`;

const passwordResetPath = (siteId: string, action: "complete" | "request") =>
  `/api/public/wbe-auth/${siteId}/password-reset/${action}`;

export const useWBEAuthSession = createQuery<
  WBEAuthSessionResponse,
  { siteId: string }
>({
  queryKey: ["useWBEAuthSession"],
  fetcher: async ({ siteId }) => {
    if (!siteId) return { authenticated: false };

    const response = await fetch(authPath(siteId, "session"), {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) return { authenticated: false };
    return response.json();
  },
});

export const useWBELogin = createMutation<
  WBEAuthSessionResponse,
  { siteId: string; body: WBEAuthLoginRequest }
>({
  mutationFn: ({ siteId, body }) =>
    api.post<WBEAuthSessionResponse>(authPath(siteId, "login"), body),
});

export const useWBESignup = createMutation<
  WBEAuthSessionResponse,
  { siteId: string; body: WBEAuthSignupRequest }
>({
  mutationFn: ({ siteId, body }) =>
    api.post<WBEAuthSessionResponse>(authPath(siteId, "signup"), body),
});

export const useWBELogout = createMutation<void, { siteId: string }>({
  mutationFn: async ({ siteId }) => {
    const response = await fetch(authPath(siteId, "logout"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error("Failed to logout");
  },
});

export const useWBEUpdateLanguage = createMutation<
  WBEAuthSessionResponse,
  { siteId: string; body: WBEAuthUpdateLanguageRequest }
>({
  mutationFn: async ({ siteId, body }) => {
    const response = await fetch(authPath(siteId, "language"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error("Failed to update language");
    return response.json();
  },
});

export const useWBEPasswordResetRequest = createMutation<
  WBEPasswordResetResponse,
  { siteId: string; body: WBEPasswordResetRequest }
>({
  mutationFn: ({ siteId, body }) =>
    api.post<WBEPasswordResetResponse>(
      passwordResetPath(siteId, "request"),
      body,
    ),
});

export const useWBEPasswordResetComplete = createMutation<
  WBEPasswordResetResponse,
  { siteId: string; body: WBEPasswordResetCompleteRequest }
>({
  mutationFn: ({ siteId, body }) =>
    api.post<WBEPasswordResetResponse>(
      passwordResetPath(siteId, "complete"),
      body,
    ),
});
